import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { CARD } from '../ui';
import StatTile from '../dashboard/StatTile';
import { percentChange } from '../dashboard/theme';
import MonthlyChart from './MonthlyChart';
import MultiSelect from './MultiSelect';
import InvoiceTable from './InvoiceTable';
import CustomersTable from './CustomersTable';
import IssueModal from './IssueModal';
import PlanModal from './PlanModal';
import { useInvoiceActions } from './InvoiceParts';
import { AgingPanel, CompanyLeaderboard, PlanMix, SegmentedControl } from './RevenueWidgets';
import {
  AGING_BUCKETS,
  BREAKDOWNS,
  EMPTY_FILTERS,
  EMPTY_FOCUS,
  STATUS_OPTIONS,
  aging,
  companyLeaderboard,
  hasFilters,
  invoiceRows,
  monthlySeries,
  planMix,
  summarize,
  toQuarters,
} from './overview';
import { seriesColor } from './theme';
import {
  addMonths,
  currentMonthKey,
  formatDollars,
  formatMonth,
  formatMonthLong,
  monthAxisLabel,
  monthsBetween,
  plural,
} from './format';

const RANGE_PRESETS = [
  { id: '3', label: '3 months', months: 3 },
  { id: '6', label: '6 months', months: 6 },
  { id: '12', label: '12 months', months: 12 },
  { id: 'ytd', label: 'Year to date' },
  { id: 'all', label: 'All time' },
];

const resolveRange = (range, earliest) => {
  const end = currentMonthKey();
  if (range.preset === 'custom') return { start: range.start, end: range.end };
  if (range.preset === 'ytd') return { start: `${end.slice(0, 4)}-01`, end };
  if (range.preset === 'all') return { start: earliest || addMonths(end, -23), end };
  const preset = RANGE_PRESETS.find((p) => p.id === range.preset) || RANGE_PRESETS[2];
  return { start: addMonths(end, -(preset.months - 1)), end };
};

// The hover background is spelled out on purpose: App.css's global
// button:hover color outranks a single Tailwind bg-* class.
const tabClass = (active) =>
  `px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 border ${
    active
      ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border-purple-400/40'
      : 'bg-gray-800/60 hover:bg-gray-700/60 text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'
  }`;

const SELECT = 'bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500';

const RangeFilter = ({ range, onChange, resolved, earliest }) => {
  const options = monthsBetween(earliest && earliest < addMonths(currentMonthKey(), -35) ? earliest : addMonths(currentMonthKey(), -35), currentMonthKey());
  const custom = range.preset === 'custom';
  return (
    <div className="flex flex-wrap items-center gap-2">
      {RANGE_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onChange({ preset: preset.id })}
          aria-pressed={range.preset === preset.id}
          className={tabClass(range.preset === preset.id)}
        >
          {preset.label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange({ preset: 'custom', start: resolved.start, end: resolved.end })}
        aria-pressed={custom}
        className={tabClass(custom)}
      >
        Custom
      </button>
      {custom && (
        <span className="flex items-center gap-2">
          <label className="sr-only" htmlFor="billing-range-start">From</label>
          <select
            id="billing-range-start"
            className={SELECT}
            value={range.start}
            onChange={(e) => onChange({ ...range, start: e.target.value, end: range.end < e.target.value ? e.target.value : range.end })}
          >
            {options.map((key) => <option key={key} value={key}>{formatMonth(key)}</option>)}
          </select>
          <span className="text-sm text-gray-500">to</span>
          <label className="sr-only" htmlFor="billing-range-end">To</label>
          <select
            id="billing-range-end"
            className={SELECT}
            value={range.end}
            onChange={(e) => onChange({ ...range, end: e.target.value, start: range.start > e.target.value ? e.target.value : range.start })}
          >
            {options.map((key) => <option key={key} value={key}>{formatMonth(key)}</option>)}
          </select>
        </span>
      )}
    </div>
  );
};

const FocusChip = ({ children, onClear }) => (
  <span className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-sm bg-purple-500/15 text-purple-200 border border-purple-400/40">
    {children}
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear"
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-transparent hover:bg-purple-500/30 p-0 text-purple-200"
    >
      ×
    </button>
  </span>
);

// The dev's revenue overview: money in, money owed, and where both come from.
// One filter row scopes the whole page; clicking the chart or the receivables
// drills the widgets below it into a month or an aging bucket.
const RevenueOverview = () => {
  const [range, setRange] = useState({ preset: '12' });
  const [earliest, setEarliest] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [focus, setFocus] = useState(EMPTY_FOCUS);
  const [breakdown, setBreakdown] = useState('type');
  const [metric, setMetric] = useState('billed');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'period', dir: 'desc' });
  const [planCompany, setPlanCompany] = useState(null);
  const [issueMonth, setIssueMonth] = useState(null);

  const { start, end } = resolveRange(range, earliest);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/overview?start=${start}&end=${end}`);
      setData(res.data);
      setEarliest(res.data.earliest_month);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not load billing.');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    load();
  }, [load, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const actions = useInvoiceActions(reload);

  // A focused month outside a new range has nothing left to show.
  useEffect(() => {
    if (focus.month && (focus.month < start || focus.month > end)) setFocus((f) => ({ ...f, month: null }));
  }, [start, end, focus.month]);

  const view = useMemo(() => {
    if (!data) return null;
    const months = monthlySeries(data, filters, breakdown);
    const byQuarter = months.length > 24;
    const columns = byQuarter
      ? toQuarters(months).map((q, index) => {
          const [year, quarter] = q.key.split('-');
          // A quarter cut short by the range names only the months it holds.
          const covered = [q.months[0], q.months[q.months.length - 1]]
            .filter((key, i, keys) => i === 0 || key !== keys[0])
            .map((key) => formatMonth(key, { month: 'short' }))
            .join('–');
          return {
            ...q,
            axisLabel: index === 0 || quarter === 'Q1' ? `${quarter} '${year.slice(2)}` : quarter,
            fullLabel: `${quarter} ${year} (${covered})`,
          };
        })
      : months.map((m, index) => ({
          ...m,
          key: m.month,
          axisLabel: monthAxisLabel(m.month, index),
          fullLabel: formatMonthLong(m.month),
        }));
    const owedByCompany = new Map();
    for (const invoice of data.invoices) {
      if (invoice.balance_cents > 0) owedByCompany.set(invoice.company_id, (owedByCompany.get(invoice.company_id) || 0) + invoice.balance_cents);
    }
    return {
      totals: summarize(data, filters),
      columns,
      byQuarter,
      buckets: aging(data, filters, focus),
      leaders: companyLeaderboard(data, filters, focus, focus.bucket ? 'owed' : metric),
      plans: planMix(data, filters, focus),
      rows: invoiceRows(data, filters, focus, query, sort),
      liveKeys: new Set(data.invoices.filter((i) => i.status !== 'void').map((i) => `${i.company_id}:${i.period}`)),
      owedByCompany,
      customers: data.companies.filter(
        (c) =>
          (!filters.companies.length || filters.companies.includes(c.id)) &&
          (!filters.plans.length || filters.plans.includes(c.subscription?.plan)),
      ),
    };
  }, [data, filters, focus, breakdown, metric, query, sort]);

  const savePlan = async (payload) => {
    try {
      await axios.put(`${API_BASE_URL}/api/billing/subscriptions/${planCompany.id}`, payload);
      setPlanCompany(null);
      reload();
      return null;
    } catch (err) {
      return err.response?.data?.msg || 'Could not save the plan.';
    }
  };

  const toggle = (key, id) =>
    setFilters((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((v) => v !== id) : [...f[key], id] }));

  if (!data) {
    return error ? (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-red-300">{error}</div>
    ) : (
      <div className={`${CARD} py-20 text-center text-gray-400`}>Loading billing…</div>
    );
  }

  const { totals, columns, byQuarter } = view;
  const span = data.range.months.length;
  const comparisonLabel = `vs previous ${span} month${span === 1 ? '' : 's'}`;
  const series = BREAKDOWNS[breakdown].series.map((s) => ({ ...s, color: seriesColor(breakdown, s.id) }));
  const bucket = AGING_BUCKETS.find((b) => b.id === focus.bucket);
  const rangeLabel = `${formatMonth(data.range.start)} – ${formatMonth(data.range.end)}`;
  const scopeLabel = focus.month
    ? `billed for ${formatMonthLong(focus.month)}`
    : bucket
      ? `open and ${bucket.label.toLowerCase()}`
      : `${rangeLabel}, plus anything still owed from before`;
  const latestUnissued = data.unissued[0];

  return (
    <div className="space-y-6">
      <IssueModal
        month={issueMonth}
        onIssued={() => {
          setIssueMonth(null);
          reload();
        }}
        onCancel={() => setIssueMonth(null)}
      />
      <PlanModal company={planCompany} plans={data.plans} onConfirm={savePlan} onCancel={() => setPlanCompany(null)} />
      {actions.modal}

      {latestUnissued && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-amber-200">
              {formatMonthLong(latestUnissued.month)} hasn't been invoiced —{' '}
              {plural(latestUnissued.company_ids.length, 'company', 'companies')} waiting
            </p>
            <p className="text-sm text-amber-200/80 mt-0.5">
              Review what each one would be billed, then issue.
              {data.unissued.length > 1 &&
                ` Also waiting: ${data.unissued.slice(1, 4).map((u) => formatMonth(u.month)).join(', ')}${data.unissued.length > 4 ? '…' : ''}.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.unissued.slice(0, 3).map((u, i) => (
              <button
                key={u.month}
                type="button"
                onClick={() => setIssueMonth(u.month)}
                className={
                  i === 0
                    ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-400/40 px-3 py-1.5 rounded-lg text-sm font-medium'
                    : 'bg-transparent hover:bg-amber-500/15 text-amber-200 border border-amber-400/25 px-3 py-1.5 rounded-lg text-sm'
                }
              >
                {i === 0 ? `Review ${formatMonth(u.month)}` : formatMonth(u.month)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* One filter row, scoping everything below it. */}
      <div className="flex flex-wrap items-center gap-3">
        <RangeFilter range={range} onChange={setRange} resolved={{ start, end }} earliest={earliest} />
        <div className="flex flex-wrap items-center gap-2 lg:pl-3 lg:border-l lg:border-gray-700">
          <MultiSelect
            label="Companies"
            allLabel="All companies"
            options={data.companies.map((c) => ({ id: c.id, label: c.name, meta: c.subscription?.plan_name }))}
            selected={filters.companies}
            onChange={(companies) => setFilters((f) => ({ ...f, companies }))}
          />
          <MultiSelect
            label="Plans"
            allLabel="All plans"
            options={data.plans.map((p) => ({ id: p.key, label: p.name }))}
            selected={filters.plans}
            onChange={(plans) => setFilters((f) => ({ ...f, plans }))}
          />
          <MultiSelect
            label="Statuses"
            allLabel="Any status"
            options={STATUS_OPTIONS}
            selected={filters.statuses}
            onChange={(statuses) => setFilters((f) => ({ ...f, statuses }))}
          />
          {hasFilters(filters) && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="bg-transparent hover:bg-gray-700/50 text-gray-400 hover:text-white px-3 py-2 rounded-lg text-sm"
            >
              Clear filters
            </button>
          )}
        </div>
        <span className="text-sm text-gray-500 lg:ml-auto" aria-live="polite">
          {loading ? 'Updating…' : rangeLabel}
        </span>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-red-300">{error}</div>}

      {/* Refetch holds the previous render rather than flashing a skeleton. */}
      <div className={`space-y-6 transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            hero
            label="Collected"
            value={formatDollars(totals.collected)}
            change={percentChange(totals.collected, totals.previousCollected)}
            comparisonLabel={comparisonLabel}
            caption={
              totals.paidShare !== null
                ? `${Math.round(totals.paidShare * 100)}% of these months' bills paid so far`
                : 'Cash received in this range.'
            }
          />
          <StatTile
            label="Billed"
            value={formatDollars(totals.billed)}
            change={percentChange(totals.billed, totals.previousBilled)}
            comparisonLabel={comparisonLabel}
            caption="Invoiced for the months in range."
          />
          <StatTile
            label="Outstanding"
            value={formatDollars(totals.outstanding)}
            comparisonLabel={`${plural(totals.openInvoices, 'open invoice')}, any month`}
            caption={
              totals.overdue > 0 ? (
                <span className="text-red-300">
                  {formatDollars(totals.overdue)} overdue across {plural(totals.overdueCompanies, 'company', 'companies')}
                </span>
              ) : (
                'Nothing overdue.'
              )
            }
          />
          <StatTile
            label={`${formatMonth(data.period.month, { month: 'long' })} so far`}
            value={formatDollars(totals.accruing)}
            comparisonLabel={totals.accruingCompanies ? `on pace for ${formatDollars(totals.projected)}` : null}
            caption={`${plural(totals.accruingCompanies, 'company', 'companies')} billing · not invoiced yet`}
          />
        </div>

        <MonthlyChart
          title="Revenue by month"
          subtitle={
            byQuarter
              ? 'Billed by quarter, with cash collected. Click a quarter to zoom in.'
              : 'Billed for each month, with the cash collected in it. Click a month to drill in.'
          }
          actions={
            <SegmentedControl
              label="Break revenue down by"
              options={Object.entries(BREAKDOWNS).map(([id, b]) => ({ id, label: b.label }))}
              value={breakdown}
              onChange={setBreakdown}
            />
          }
          columns={columns}
          series={series}
          showCollected
          selectedKey={byQuarter ? null : focus.month}
          selectLabel={byQuarter ? 'Zoom in to' : 'Drill into'}
          onSelect={(key) => {
            if (byQuarter) {
              const quarter = columns.find((c) => c.key === key);
              setRange({ preset: 'custom', start: quarter.months[0], end: quarter.months[quarter.months.length - 1] });
            } else {
              setFocus((f) => ({ ...f, month: f.month === key ? null : key }));
            }
          }}
          footnote="The current month is drawn lighter: it's a running estimate, not invoiced yet. Bills are dated by the month they cover; cash by the day it arrived."
        />

        {(focus.month || focus.bucket) && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-400">Showing</span>
            {focus.month && (
              <FocusChip onClear={() => setFocus((f) => ({ ...f, month: null }))}>{formatMonthLong(focus.month)}</FocusChip>
            )}
            {bucket && <FocusChip onClear={() => setFocus((f) => ({ ...f, bucket: null }))}>{bucket.label}</FocusChip>}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <AgingPanel
            buckets={view.buckets}
            selected={focus.bucket}
            onSelect={(id) => setFocus((f) => ({ ...f, bucket: id }))}
          />
          <CompanyLeaderboard
            rows={view.leaders}
            metric={metric}
            onMetric={setMetric}
            metricLocked={Boolean(focus.bucket)}
            selected={filters.companies}
            onToggle={(id) => toggle('companies', id)}
          />
          <PlanMix rows={view.plans} selected={filters.plans} onToggle={(id) => toggle('plans', id)} />
        </div>

        <InvoiceTable
          rows={view.rows}
          sort={sort}
          onSort={setSort}
          query={query}
          onQuery={setQuery}
          actions={actions}
          liveKeys={view.liveKeys}
          scopeLabel={scopeLabel}
          version={version}
        />

        <CustomersTable companies={view.customers} owedByCompany={view.owedByCompany} onSetPlan={setPlanCompany} />
      </div>
    </div>
  );
};

export default RevenueOverview;
